// iter 186 - Admin email template preview.
// Renders every onboarding email variant (24h + 72h all tier branches)
// in-browser so copy can be reviewed before anything ships. Uses the
// same render helpers as production sends, so what you see here is
// byte-identical to what customers receive.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminEmailPreview = () => {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState('');
  const [firstName, setFirstName] = useState('Francisco');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/email-preview/templates`, { credentials: 'include' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (cancelled) return;
        setTemplates(data.templates || []);
        setSelected((data.templates || [])[0]?.id || '');
      } catch (e) {
        if (!cancelled) setError(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const meta = templates.find((t) => t.id === selected);
  const iframeSrc = selected
    ? `${API}/api/admin/email-preview/render/${selected}?first_name=${encodeURIComponent(firstName || 'Test')}`
    : '';

  return (
    <div className="min-h-screen bg-[#F9F6EF] text-[#0A1F2E]" data-testid="admin-email-preview-page">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif italic text-3xl font-light mb-1">Email Template Preview</h1>
            <p className="text-sm text-[#5A6B73]">Renders the exact HTML customers receive. No send, no DB writes.</p>
          </div>
          <Link
            to="/admin/dashboard"
            className="text-xs uppercase tracking-widest text-[#1A5C6E] hover:text-[#0A1F2E]"
            data-testid="back-to-dashboard"
          >
            ← Dashboard
          </Link>
        </div>

        {loading && <p className="text-sm text-[#5A6B73]">Loading templates…</p>}
        {error && (
          <p className="text-sm text-red-600" data-testid="load-error">
            Could not load templates: {error}
          </p>
        )}

        {!loading && !error && (
          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
            <aside className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#5A6B73] mb-2">Template</label>
                <select
                  value={selected}
                  onChange={(e) => setSelected(e.target.value)}
                  className="w-full border border-[#C4B9A0] rounded px-3 py-2 bg-white text-sm"
                  data-testid="template-select"
                >
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#5A6B73] mb-2">First name variable</label>
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Test"
                  className="w-full border border-[#C4B9A0] rounded px-3 py-2 bg-white text-sm"
                  data-testid="first-name-input"
                />
              </div>
              {meta && (
                <div className="bg-[#F4ECD8] border border-[#C4B9A0] rounded p-4 text-sm space-y-2" data-testid="template-meta">
                  <div><span className="text-[#5A6B73]">Subject:</span> <span className="font-medium">{meta.subject}</span></div>
                  <div><span className="text-[#5A6B73]">Trigger:</span> {meta.trigger}</div>
                  <div><span className="text-[#5A6B73]">Tier variants:</span> {meta.tier_variants.join(', ')}</div>
                </div>
              )}
              <p className="text-xs text-[#5A6B73] leading-relaxed">
                Source of truth: <code className="bg-white px-1 rounded">scheduler/email_scheduler.py</code>
                {' · '}<code className="bg-white px-1 rounded">render_24hr_reminder_html</code>
                {' + '}<code className="bg-white px-1 rounded">render_72hr_followup_html</code>
              </p>
            </aside>

            <main className="bg-white border border-[#C4B9A0] rounded overflow-hidden">
              <div className="border-b border-[#C4B9A0] px-4 py-2 flex items-center justify-between text-xs uppercase tracking-widest text-[#5A6B73]">
                <span>Rendered email preview</span>
                <a
                  href={iframeSrc}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#1A5C6E] hover:text-[#0A1F2E]"
                  data-testid="open-in-new-tab"
                >
                  Open in new tab ↗
                </a>
              </div>
              <iframe
                title="email-preview"
                src={iframeSrc}
                className="w-full"
                style={{ height: '80vh', border: 0, background: '#F9F6EF' }}
                data-testid="email-preview-iframe"
              />
            </main>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEmailPreview;
